import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { Plus, TrendingUp, Package, Eye, DollarSign, Landmark, FileSpreadsheet, Pause, Play, PenLine, Share2, Rocket, Clapperboard, Radar } from "lucide-react";
import ListedAgo from "@/components/ListedAgo";
import { EditProductDialog } from "@/components/EditProductDialog";
import { ShareCardDialog } from "@/components/ShareCardDialog";
import { BoostDialog } from "@/components/BoostDialog";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import NoGuaranteeNotice from "@/components/NoGuaranteeNotice";
import { SellingUnlockCard } from "@/components/SellingUnlockCard";
import { ProofCard } from "@/components/ProofCard";
import { useAuth } from "@/contexts/AuthContext";
import PromoteProduct from "@/components/PromoteProduct";
import { DemandRadarCard } from "@/components/DemandRadarCard";
import { TrendingWantsCard } from "@/components/TrendingWantsCard";
import { WhatsAppBroadcastCard } from "@/components/WhatsAppBroadcastCard";
import { TID } from "@/constants/tid";
import { fmtPrice } from "@/lib/currency";

export default function SellerDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [mine, setMine] = useState([]);
  const [payouts, setPayouts] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/products/mine").then(({ data }) => setMine(data)).catch(() => {});

  useEffect(() => {
    (async () => {
      const [s, m, p] = await Promise.all([
        api.get("/analytics/seller"),
        api.get("/products/mine"),
        api.get("/payouts/status").catch(() => ({ data: { connected: false } })),
      ]);
      setStats(s.data);
      setMine(m.data);
      setPayouts(p.data);
      setLoading(false);
    })();
  }, []);

  if (loading || !stats) return <div className="p-10 text-neutral-500">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#007AFF]">Seller command center</div>
          <h1 className="mt-2 font-black tracking-tight text-4xl">Your live performance</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/seller/stage" data-testid="seller-stage-link">
            <Button variant="outline" className="rounded-full h-11 px-5 bg-transparent border-[#FF3B30]/50 text-white hover:bg-[#FF3B30]/10 hover:text-white font-semibold">
              <Clapperboard className="h-4 w-4 mr-1" /> My Stage
            </Button>
          </Link>
          <Link to="/seller/buyers" data-testid="seller-buyers-link">
            <Button variant="outline" className="rounded-full h-11 px-5 bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white font-semibold">
              <Radar className="h-4 w-4 mr-1" /> Buyer Finder
            </Button>
          </Link>
          <Link to="/seller/social" data-testid="seller-social-link">
            <Button variant="outline" className="rounded-full h-11 px-5 bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white font-semibold">
              <Share2 className="h-4 w-4 mr-1" /> Social
            </Button>
          </Link>
          <Link to="/earnings" data-testid="seller-earnings-link">
            <Button variant="outline" className="rounded-full h-11 px-5 bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white font-semibold">
              <DollarSign className="h-4 w-4 mr-1" /> Earnings
            </Button>
          </Link>
          <Link to="/seller/bulk" data-testid="seller-bulk-link">
            <Button variant="outline" className="rounded-full h-11 px-5 bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white font-semibold">
              <FileSpreadsheet className="h-4 w-4 mr-1" /> Bulk upload
            </Button>
          </Link>
          <Link to="/seller/new" data-testid={TID.sellerCreateBtn}>
            <Button className="bg-white text-black hover:bg-neutral-200 rounded-full h-11 px-5 font-semibold">
              <Plus className="h-4 w-4 mr-1" /> New product
            </Button>
          </Link>
        </div>
      </div>

      {payouts && !payouts.any_channel && (
        <Link
          to="/payouts"
          data-testid="payouts-connect-banner"
          className="mt-6 flex items-start gap-3 rounded-xl border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] p-5 hover:border-[#FF3B30]/70"
          style={{ transitionProperty: "border-color", transitionDuration: "200ms" }}
        >
          <div className="h-9 w-9 rounded-md bg-[#FF3B30]/10 border border-[#FF3B30]/30 inline-flex items-center justify-center flex-shrink-0">
            <Landmark className="h-4 w-4 text-[#FF3B30]" />
          </div>
          <div>
            <div className="font-bold text-white">Connect a payment channel to start selling</div>
            <p className="text-sm text-neutral-400 mt-1">
              Buyers can't purchase your products until you connect your bank (Paystack) or Stripe.
              Sales settle directly to you — VISMARKS's fee (0.5% per item sold) is deducted upfront. <span className="text-[#FF3B30] font-semibold">Set up now →</span>
            </p>
          </div>
        </Link>
      )}

      <NoGuaranteeNotice className="mt-6" />

      <ProofCard />
      <SellingUnlockCard />

      <OnboardingChecklist user={user} hasProducts={mine.length > 0} payoutsConnected={payouts?.any_channel} />

      <DemandRadarCard />
      <TrendingWantsCard />

      <WhatsAppBroadcastCard products={mine} />

      <div data-testid={TID.sellerStats} className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={Package} label="Live products" value={stats.live_products} accent="text-[#34C759]" sub={`of ${stats.total_products} total`} />
        <Stat icon={Eye} label="Total views" value={stats.total_views} />
        <Stat icon={DollarSign} label="Revenue 24h" value={`$${stats.revenue_24h.toFixed(2)}`} accent="text-[#007AFF]" />
        <Stat icon={TrendingUp} label="Conversions 24h" value={stats.conversions_24h} />
        <Stat
          icon={DollarSign}
          label="Platform fees owed"
          value={`$${(stats.platform_fees_owed || 0).toFixed(2)}`}
          accent="text-[#FF3B30]"
          sub="0.5% per item sold"
        />
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-[#27272A] bg-[#121212] p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500">Views &amp; sales &mdash; last 24h</div>
              <h3 className="font-black text-xl tracking-tight mt-1">Hourly momentum</h3>
            </div>
          </div>
          <div className="mt-6 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.hourly}>
                <CartesianGrid stroke="#27272A" vertical={false} />
                <XAxis dataKey="hour" stroke="#71717a" fontSize={11} tick={{ fontFamily: "monospace" }} />
                <YAxis stroke="#71717a" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A" }} labelStyle={{ color: "#fff" }} />
                <Line type="monotone" dataKey="views" stroke="#007AFF" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="sales" stroke="#34C759" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-[#27272A] bg-[#121212] p-6">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Top products</div>
          <h3 className="font-black text-xl tracking-tight mt-1">By views</h3>
          <ul className="mt-4 space-y-3">
            {stats.top_products.length === 0 && (
              <li className="text-neutral-500 text-sm">No data yet.</li>
            )}
            {stats.top_products.map((p, i) => (
              <li key={p.id} className="flex items-center gap-3">
                <span className="font-mono tabular-nums text-neutral-500 text-xs w-4">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{p.title}</div>
                  <div className="text-xs text-neutral-500 font-mono">
                    {p.views} views &middot; {p.purchases} sold
                  </div>
                </div>
                <div className="font-mono text-sm">${p.revenue.toFixed(0)}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div data-testid={TID.sellerProducts} className="mt-10">
        <h2 className="font-black text-2xl tracking-tight mb-4">Your products</h2>
        {mine.length === 0 ? (
          <div className="border border-dashed border-[#27272A] rounded-xl p-12 text-center">
            <div className="font-bold text-lg">No products yet</div>
            <div className="text-neutral-400 text-sm mt-2">Publish your first listing — it goes live to buyers instantly and stays live.</div>
            <Link to="/seller/new" className="inline-block mt-6">
              <Button className="bg-white text-black hover:bg-neutral-200 rounded-full">
                <Plus className="h-4 w-4 mr-1" /> Create first product
              </Button>
            </Link>
          </div>
        ) : (
          <div className="rounded-xl border border-[#27272A] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-xs uppercase tracking-widest text-neutral-500">
                <tr>
                  <th className="text-left px-4 py-3">Product</th>
                  <th className="text-left px-4 py-3">Category</th>
                  <th className="text-right px-4 py-3">Views</th>
                  <th className="text-right px-4 py-3">Sold</th>
                  <th className="text-right px-4 py-3">Price</th>
                  <th className="text-right px-4 py-3">Listed</th>
                  <th className="text-right px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {mine.map((p) => (
                  <ProductRow key={p.id} p={p} onChanged={(np) => (np?.id ? setMine((m) => m.map((x) => (x.id === np.id ? np : x))) : load())} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {mine.length > 0 && <PromoteProduct products={mine} />}
    </div>
  );
}

function ProductRow({ p, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [boosting, setBoosting] = useState(false);
  const boostActive = p.boost?.ends_at && new Date(p.boost.ends_at) > new Date();
  const paused = p.status === "paused";

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/products/${p.id}/visibility`, { action: paused ? "activate" : "pause" });
      onChanged(data);
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-t border-[#27272A] hover:bg-[#121212]">
      <td className="px-4 py-3">
        <Link to={`/product/${p.id}`} className={`hover:text-[#007AFF] ${paused ? "text-neutral-500" : ""}`}>{p.title}</Link>
      </td>
      <td className="px-4 py-3 text-neutral-400">{p.category}</td>
      <td className="px-4 py-3 text-right font-mono">{p.views}</td>
      <td className="px-4 py-3 text-right font-mono">{p.purchases}</td>
      <td className="px-4 py-3 text-right font-mono">{fmtPrice(p.price, p.currency)}</td>
      <td className="px-4 py-3 text-right"><ListedAgo publishedAt={p.published_at} small /></td>
      <td className="px-4 py-3 text-right">
        <span
          data-testid="product-status-badge"
          className={`px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold border ${
            paused
              ? "text-[#FF9F0A] border-[#FF9F0A]/40 bg-[#FF9F0A]/10"
              : p.status === "sold_out"
              ? "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10"
              : "text-[#34C759] border-[#34C759]/40 bg-[#34C759]/10"
          }`}
        >
          {paused ? "Paused" : p.status === "sold_out" ? "Sold out" : "Live"}
        </span>
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <Link
          to={`/seller/buyers?product=${p.id}`}
          data-testid="product-find-buyers-btn"
          title="Find buyers for this product with Visibility Intelligence"
          aria-label="Find buyers"
          className="inline-flex p-1.5 rounded-md transition-colors text-neutral-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
        >
          <Radar className="h-4 w-4" />
        </Link>
        <button
          onClick={() => setBoosting(true)}
          data-testid="product-boost-btn"
          title={boostActive ? "Boost running — extend" : "Boost in buyer feeds for 24h"}
          aria-label="Boost listing"
          className={`p-1.5 rounded-md transition-colors ${boostActive ? "text-[#FF3B30] bg-[#FF3B30]/10" : "text-neutral-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"}`}
        >
          <Rocket className="h-4 w-4" />
        </button>
        <button
          onClick={() => setSharing(true)}
          data-testid="product-share-card-btn"
          title={p.views >= 50 ? `Share your ${p.views} views card` : "Share card"}
          aria-label="Share card"
          className={`ml-1 p-1.5 rounded-md transition-colors ${p.views >= 50 ? "text-[#34C759] hover:bg-[#34C759]/10" : "text-neutral-400 hover:text-white hover:bg-white/10"}`}
        >
          <Share2 className="h-4 w-4" />
        </button>
        <button
          onClick={() => setEditing(true)}
          data-testid="product-edit-btn"
          title="Edit product"
          aria-label="Edit product"
          className="ml-1 p-1.5 rounded-md text-neutral-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          <PenLine className="h-4 w-4" />
        </button>
        <button
          onClick={toggle}
          disabled={busy}
          data-testid="product-pause-btn"
          title={paused ? "Re-activate listing" : "Pause listing"}
          aria-label={paused ? "Re-activate listing" : "Pause listing"}
          className={`ml-1 p-1.5 rounded-md transition-colors ${paused ? "text-[#34C759] hover:bg-[#34C759]/10" : "text-[#FF9F0A] hover:bg-[#FF9F0A]/10"}`}
        >
          {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
        </button>
        {editing && <EditProductDialog product={p} open={editing} onOpenChange={setEditing} onSaved={onChanged} />}
        {sharing && <ShareCardDialog product={p} open={sharing} onOpenChange={setSharing} />}
        {boosting && <BoostDialog product={p} open={boosting} onOpenChange={setBoosting} onBoosted={onChanged} />}
      </td>
    </tr>
  );
}

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="rounded-xl border border-[#27272A] bg-[#121212] p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
        <Icon className="h-4 w-4 text-neutral-500" />
      </div>
      <div className={`mt-3 font-mono tabular-nums font-bold text-3xl ${accent || "text-white"}`}>{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}
